import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const playChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [[523.25, 0], [783.99, 0.18], [1046.5, 0.36]].forEach(([freq, at]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(0.07, now + at + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 1.4);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + at);
      osc.stop(now + at + 1.5);
    });
  } catch {
    /* audio unavailable — stay silent */
  }
};

export const IntroSplash = ({ onDone }) => {
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("pk_sound") !== "off");
  const played = useRef(false);

  const finish = () => {
    sessionStorage.setItem("pk_intro", "1");
    onDone();
  };

  useEffect(() => {
    if (soundOn && !played.current) {
      played.current = true;
      playChime();
    }
    const t = setTimeout(finish, 2400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSound = (e) => {
    e.stopPropagation();
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("pk_sound", next ? "on" : "off");
    if (next) playChime();
  };

  return (
    <motion.div
      data-testid="intro-splash"
      className="fixed inset-0 z-[100] bg-beet-deep flex flex-col items-center justify-center cursor-pointer overflow-hidden"
      onClick={finish}
      exit={{ y: "-100%" }}
      transition={{ duration: 0.85, ease: EASE }}
      aria-label="Pranivaa Kitchens intro"
    >
      <div
        className="absolute inset-0 opacity-[0.14]"
        style={{ backgroundImage: "radial-gradient(circle at 50% 42%, #C6A052 0%, transparent 55%)" }}
        aria-hidden="true"
      />
      <motion.div
        className="rounded-full border border-turmeric/50 p-3 md:p-4"
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.9, ease: EASE }}
      >
        <div className="rounded-full border border-turmeric/25 p-2">
          <motion.img
            src="/logo.png"
            alt="Pranivaa Kitchens"
            className="w-40 h-40 md:w-56 md:h-56 rounded-full object-cover shadow-2xl"
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1, delay: 0.15, ease: EASE }}
          />
        </div>
      </motion.div>
      <motion.div
        className="h-px w-24 bg-turmeric/70 my-6 origin-center"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
      />
      <span className="block overflow-hidden">
        <motion.p
          className="font-serif italic text-white/85 text-lg md:text-xl tracking-wide"
          initial={{ y: "110%" }}
          animate={{ y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
        >
          Real food. Real science. Real prana.
        </motion.p>
      </span>
      <motion.p
        className="mt-3 font-mono text-[10px] tracking-[0.3em] uppercase text-white/35"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
      >
        Delhi NCR · Bengaluru
      </motion.p>
      <motion.button
        data-testid="splash-sound-toggle"
        onClick={toggleSound}
        aria-label={soundOn ? "Mute intro sound" : "Enable intro sound"}
        aria-pressed={soundOn}
        className="absolute bottom-6 right-6 w-10 h-10 rounded-full border border-white/20 text-white/60 flex items-center justify-center transition-colors hover:border-turmeric hover:text-turmeric"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.5 }}
      >
        {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
      </motion.button>
    </motion.div>
  );
};
